import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PBRMaterial as BJSPBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { ERR_INVALID_ARGUMENT } from "../../../utils/error";
import { eq } from "../../../utils/math";
import { isNotNullOrEmpty } from "../../../utils/string";
import { deserializeColor3, serializeColor3 } from "../../io";
import { PbrMaterial } from "../PbrMaterial";
import { deserializeTexture, seserializeTexture } from "./TextureSerialization";
/** @internal */
export function serializePbrMaterial(pContext, pMaterial) {
    if (pContext == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tIOMat = {};
    //id
    tIOMat.id = pMaterial.id;
    //type
    tIOMat.type = "pbr";
    //name
    if (pMaterial.name != pMaterial.id && isNotNullOrEmpty(pMaterial.name))
        tIOMat.name = pMaterial.name;
    //albedo
    if (!pMaterial.albedoColor.equalsFloats(1, 1, 1))
        tIOMat.albedo = serializeColor3(pMaterial.albedoColor);
    //emissive
    if (!pMaterial.emissiveColor.equalsFloats(0, 0, 0))
        tIOMat.emissive = serializeColor3(pMaterial.emissiveColor);
    //metallic
    if (pMaterial.metallic != null && !eq(pMaterial.metallic, 1))
        tIOMat.metallic = pMaterial.metallic;
    //roughness
    if (pMaterial.roughness != null && !eq(pMaterial.roughness, 1))
        tIOMat.roughness = pMaterial.roughness;
    //alpha
    if (!eq(pMaterial.alpha, 1))
        tIOMat.alpha = pMaterial.alpha;
    //alpha test
    if (pMaterial.transparencyMode === BJSPBRMaterial.PBRMATERIAL_ALPHATEST) {
        tIOMat.alphaTest = true;
    }
    //albedo texture
    if (pMaterial.albedoTexture != null) {
        const tIOTex = seserializeTexture(pContext, pMaterial.albedoTexture);
        if (tIOTex != null)
            tIOMat.albedoTexture = tIOTex;
    }
    //bump texture
    if (pMaterial.bumpTexture != null) {
        const tIOTex = seserializeTexture(pContext, pMaterial.bumpTexture);
        if (tIOTex != null)
            tIOMat.bumpTexture = tIOTex;
    }
    //metallic texture
    if (pMaterial.metallicTexture != null) {
        const tIOTex = seserializeTexture(pContext, pMaterial.metallicTexture);
        if (tIOTex != null) {
            tIOMat.metallic = undefined;
            tIOMat.metallicTexture = tIOTex;
        }
    }
    //roughness texture
    if (pMaterial.microSurfaceTexture != null) {
        const tIOTex = seserializeTexture(pContext, pMaterial.microSurfaceTexture);
        if (tIOTex != null) {
            tIOMat.roughness = undefined;
            tIOMat.roughnessTexture = tIOTex;
        }
    }
    return (tIOMat);
}
/** @internal */
export function deserializePbrMaterial(pIOMaterial, pContext, pMatManager) {
    if (pIOMaterial.type == null || pIOMaterial.type === "std")
        return (deserializeLegacyMaterial(pIOMaterial, pContext, pMatManager));
    if (pContext == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    //id
    const tMat = new PbrMaterial(pIOMaterial.id, pMatManager);
    //name
    if (pIOMaterial.name != null)
        tMat.name = pIOMaterial.name;
    //albedo
    if (pIOMaterial.albedo != null)
        tMat.albedoColor = deserializeColor3(pIOMaterial.albedo);
    //emissive
    if (pIOMaterial.emissive != null)
        tMat.emissiveColor = deserializeColor3(pIOMaterial.emissive);
    //metallic
    if (pIOMaterial.metallic != null)
        tMat.metallic = pIOMaterial.metallic;
    else
        tMat.metallic = 1.0;
    //roughness
    if (pIOMaterial.roughness != null) {
        tMat.roughness = pIOMaterial.roughness;
        if (pContext.documentHeader.majorVer === 2 && pContext.documentHeader.minorVer === 0) {
            //before version 2.1 the roughness value had to be modified in order to get correct results
            //becasue of a wrongly converted env-map
            //here we convert the roughness back to the initial value (approximately)
            tMat.roughness = 1.6 * tMat.roughness - 0.6 * Math.pow(tMat.roughness, 2);
        }
    }
    else
        tMat.roughness = 1.0;
    //alpha
    if (pIOMaterial.alpha != null)
        tMat.alpha = pIOMaterial.alpha;
    //albedo texture
    if (pIOMaterial.albedoTexture != null) {
        tMat.albedoTexture = deserializeTexture(pIOMaterial.albedoTexture, pContext, pMatManager);
        if (tMat.albedoTexture != null && tMat.albedoTexture.hasAlpha) {
            tMat.useAlphaFromAlbedoTexture = true;
            if (pIOMaterial.alphaTest === true) {
                tMat.transparencyMode = BJSPBRMaterial.PBRMATERIAL_ALPHATEST;
            }
        }
    }
    //bump texture
    if (pIOMaterial.bumpTexture != null) {
        tMat.bumpTexture = deserializeTexture(pIOMaterial.bumpTexture, pContext, pMatManager);
        if (tMat.bumpTexture != null)
            tMat.forceIrradianceInFragment = true;
    }
    //metallic texture
    if (pIOMaterial.metallicTexture != null) {
        tMat.metallicTexture = deserializeTexture(pIOMaterial.metallicTexture, pContext, pMatManager);
    }
    //metallic texture
    if (pIOMaterial.roughnessTexture != null) {
        tMat.microSurfaceTexture = deserializeTexture(pIOMaterial.roughnessTexture, pContext, pMatManager);
    }
    tMat.freeze(); //better performance, materials do not change
    return (tMat);
}
//// internals /////////////////////////////////////////////////////////////////////////////////////
function deserializeLegacyMaterial(pIOMaterial, pContext, pMatManager) {
    if (pContext == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    //id
    const tMat = new PbrMaterial(pIOMaterial.id, pMatManager);
    //name
    if (pIOMaterial.name != null)
        tMat.name = pIOMaterial.name;
    //diffuse
    let tDiffuseColor = Color3.White();
    if (pIOMaterial.diffuse != null) {
        tDiffuseColor = deserializeColor3(pIOMaterial.diffuse);
    }
    //specular
    let tSpecularColor = Color3.Black();
    if (pIOMaterial.specular != null)
        tSpecularColor = deserializeColor3(pIOMaterial.specular);
    //emissive
    if (pIOMaterial.emissive != null)
        tMat.emissiveColor = deserializeColor3(pIOMaterial.emissive);
    //shininess
    let tShininess = 30;
    if (pIOMaterial.shininess != null)
        tShininess = pIOMaterial.shininess;
    //reflectivity
    let tReflectivity = 0.0;
    if (pIOMaterial.reflectivity != null)
        tReflectivity = pIOMaterial.reflectivity;
    //alpha
    if (pIOMaterial.alpha != null)
        tMat.alpha = pIOMaterial.alpha;
    //diffuse texture -> albedo texture
    if (pIOMaterial.diffuseTexture != null) {
        tMat.albedoTexture = deserializeTexture(pIOMaterial.diffuseTexture, pContext, pMatManager);
        if (tMat.albedoTexture != null && tMat.albedoTexture.hasAlpha)
            tMat.useAlphaFromAlbedoTexture = true;
    }
    //bump texture
    if (pIOMaterial.bumpTexture != null) {
        tMat.bumpTexture = deserializeTexture(pIOMaterial.bumpTexture, pContext, pMatManager);
        if (tMat.bumpTexture != null)
            tMat.forceIrradianceInFragment = true;
    }
    //get PBR parmaters
    const tParams = PbrMaterial.ConvertToPbrParameters(tDiffuseColor, tMat.albedoTexture != null, tSpecularColor, tShininess, tReflectivity, tMat.alpha);
    tMat.albedoColor = tParams.albedo;
    tMat.metallic = tParams.metallic;
    tMat.roughness = tParams.roughness;
    tMat.freeze(); //better performance, materials do not change
    return (tMat);
}
